import { apiClient } from '@/api/http/client'
import { ApiError, type ApiResponse } from '@/api/http/types'
import type {
  FreshnessTag,
  IProfileAttribute,
  IProfileAttributeSimilarMatch,
  ProfileSystemKey,
} from '@brushfire/types'
import { systemProfileAttributesAsPublic } from '@brushfire/types'
import type { CreateQuestionBankEntryInput } from '@brushfire/types'

function isAttributeRow(row: unknown): row is IProfileAttribute {
  if (!row || typeof row !== 'object') return false
  const candidate = row as Partial<IProfileAttribute> & { _id?: unknown }
  const name = candidate.name
  const options = candidate.options
  return typeof name === 'string' && name.length > 0 && Array.isArray(options)
}

function normalizeAttributeRow(row: unknown): IProfileAttribute | null {
  if (!isAttributeRow(row)) return null
  const raw = row as IProfileAttribute & { _id?: unknown }
  const id =
    typeof raw.id === 'string' && raw.id.length > 0
      ? raw.id
      : typeof raw._id === 'string' && raw._id.length > 0
        ? raw._id
        : raw.systemKey
  if (!id) return null
  return {
    id,
    name: raw.name,
    freshnessTag: raw.freshnessTag ?? 'PERM',
    options: raw.options.filter((option) => typeof option === 'string' && option.length > 0),
    systemKey: raw.systemKey ?? null,
    questionBankEntryId: raw.questionBankEntryId ?? null,
  }
}

export function unwrapProfileAttributeList(envelope: unknown): IProfileAttribute[] {
  if (Array.isArray(envelope)) {
    return envelope.map(normalizeAttributeRow).filter((row): row is IProfileAttribute => row != null)
  }
  if (!envelope || typeof envelope !== 'object') return []
  const root = envelope as { data?: unknown; attributes?: unknown }
  const payload = root.data !== undefined ? root.data : root
  if (Array.isArray(payload)) {
    return payload.map(normalizeAttributeRow).filter((row): row is IProfileAttribute => row != null)
  }
  if (payload && typeof payload === 'object' && 'attributes' in payload) {
    const attributes = (payload as { attributes?: unknown }).attributes
    if (Array.isArray(attributes)) {
      return attributes.map(normalizeAttributeRow).filter((row): row is IProfileAttribute => row != null)
    }
  }
  if (Array.isArray(root.attributes)) {
    return root.attributes.map(normalizeAttributeRow).filter((row): row is IProfileAttribute => row != null)
  }
  return []
}

export function withSystemProfileAttributes(rows: readonly IProfileAttribute[]): IProfileAttribute[] {
  const seen = new Set(
    rows
      .map((row) => row.systemKey)
      .filter((key): key is NonNullable<IProfileAttribute['systemKey']> => key != null),
  )
  return [
    ...rows,
    ...systemProfileAttributesAsPublic().filter((row) => row.systemKey && !seen.has(row.systemKey)),
  ]
}

function matchesAttributeSearch(row: IProfileAttribute, term: string): boolean {
  const query = term.trim().toLowerCase()
  if (!query) return true
  return row.name.toLowerCase().includes(query) || (row.systemKey?.toLowerCase().includes(query) ?? false)
}

export async function listProfileAttributes(search?: string): Promise<IProfileAttribute[]> {
  try {
    const { data } = await apiClient.get<ApiResponse<{ attributes: IProfileAttribute[] } | IProfileAttribute[]>>(
      '/api/v1/profile-attributes',
      { params: search ? { search } : undefined },
    )
    const merged = withSystemProfileAttributes(unwrapProfileAttributeList(data))
    return search ? merged.filter((row) => matchesAttributeSearch(row, search)) : merged
  } catch (err) {
    if (err instanceof ApiError && (err.isUnauthorized || err.isForbidden)) {
      throw err
    }
    const fallback = withSystemProfileAttributes([])
    return search ? fallback.filter((row) => matchesAttributeSearch(row, search)) : fallback
  }
}

function isSimilarMatch(row: unknown): row is IProfileAttributeSimilarMatch {
  if (!row || typeof row !== 'object') return false
  const candidate = row as Partial<IProfileAttributeSimilarMatch>
  return (
    typeof candidate.profileAttributeId === 'string' &&
    candidate.profileAttributeId.length > 0 &&
    typeof candidate.prompt === 'string' &&
    typeof candidate.attributeName === 'string'
  )
}

export function unwrapSimilarProfileMatch(envelope: unknown): IProfileAttributeSimilarMatch | null {
  if (!envelope || typeof envelope !== 'object') return null
  const root = envelope as { data?: unknown; match?: unknown }
  const payload = root.data !== undefined ? root.data : root
  if (payload && typeof payload === 'object' && 'match' in payload) {
    const match = (payload as { match: unknown }).match
    return isSimilarMatch(match) ? match : null
  }
  return isSimilarMatch(payload) ? payload : null
}

export async function findSimilarProfileAttribute(input: {
  prompt: string
  attributeName?: string
}): Promise<IProfileAttributeSimilarMatch | null> {
  try {
    const { data } = await apiClient.post<
      ApiResponse<{ match: IProfileAttributeSimilarMatch | null } | IProfileAttributeSimilarMatch>
    >('/api/v1/profile-attributes/similar', input)
    return unwrapSimilarProfileMatch(data)
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthorized) {
      throw err
    }
    return null
  }
}

export async function createProfileAttribute(input: {
  name: string
  freshnessTag: FreshnessTag
  prompt: string
  type: CreateQuestionBankEntryInput['type']
  question: CreateQuestionBankEntryInput['question']
}): Promise<IProfileAttribute> {
  const { data } = await apiClient.post<ApiResponse<IProfileAttribute>>(
    '/api/v1/profile-attributes',
    input,
  )
  if (!data.data) throw new Error('Profile attribute was not created')
  return data.data
}

export async function updateProfileAttribute(
  id: string,
  input: {
    name?: string
    prompt: string
    type: CreateQuestionBankEntryInput['type']
    question: CreateQuestionBankEntryInput['question']
  },
): Promise<IProfileAttribute> {
  const { data } = await apiClient.put<ApiResponse<IProfileAttribute>>(
    `/api/v1/profile-attributes/${encodeURIComponent(id)}`,
    input,
  )
  if (!data.data) throw new Error('Profile attribute was not updated')
  return data.data
}

export const FRESHNESS_CHOICES: ReadonlyArray<{ tag: FreshnessTag; label: string }> = [
  { tag: 'PERM', label: 'PERM - No scheduled refresh' },
  { tag: 'R1M', label: 'R1M - Refresh after 1 month' },
  { tag: 'R3M', label: 'R3M - Refresh after 3 months' },
  { tag: 'R6M', label: 'R6M - Refresh after 6 months' },
  { tag: 'R12M', label: 'R12M - Refresh after 12 months' },
]

export function isProfileBuilderType(type: string): boolean {
  return (
    type === 'MULTIPLE_CHOICE' ||
    type === 'YES_NO' ||
    type === 'CHECKBOX' ||
    type === 'DROPDOWN' ||
    type === 'SHORT_TEXT'
  )
}

export type ProfileLink = {
  profileAttributeId: string
  freshnessTag: FreshnessTag
  systemKey: ProfileSystemKey | null
  questionBankEntryId: string
}
